import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import '@css/pages/community/CommunityEvents.scss'
import { Button, Nav } from 'react-bootstrap'
import { usePermissions } from '~/pages/posts/PostUtils'
import { useCommunity } from '~/contexts/CommunityContext'
import { useAuth } from '~/auth/Auth'
import {
  EventPartsFragment,
  GetCommunityPastEventsDocument,
  GetCommunityUpcomingEventsDocument,
  GetNotificationsDocument,
  MarkEventsViewedUpToDocument,
  Maybe,
  NotificationType,
} from '~/api/generated/graphql'
import CommunityEventEdit from '~/pages/community/events/CommunityEventEdit'
import CommunityEventRow from '~/pages/community/events/CommunityEventRow'
import ToastComponent from '~/common/ToastComponent'
import { asDate, asNotification, getStartOfCurrentDay } from '~/utils'
import { elementClicked } from '~/common/EventLogger'
import { SizeBreakpoint, useWindowSize } from '~/common/hooks/useWindowSize'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import LiveStreamQA from '~/pages/community/events/LiveStreamQA'
import { eventHasStartHandler } from '~/common/utils'
import { useNavigate } from 'react-router'

type CommunityEventsProps = {
  isCompany: boolean
}

export enum EventTabs {
  Upcoming,
  Past,
  LiveStream,
}

const CommunityEvents = ({ isCompany }: CommunityEventsProps) => {
  const { hasLeaderPermissions, loading: memberLoading } = usePermissions()
  const { state } = useLocation()
  const { authUserId, isVeevan, profileVisible, loading: authLoading, actingSummitAdmin, actingSysAdmin } = useAuth()
  const { communityId, isPrivate } = useCommunity()
  const { isCondensedPortrait: isMobile, breakpoint } = useWindowSize()
  const canStartLivestream = useMemo(() => {
    return state?.doJoinLivestream && navigator?.userActivation?.isActive
  }, [state])
  const [tab, setTab] = useState(canStartLivestream ? EventTabs.LiveStream : EventTabs.Upcoming)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Event links in this format could still exist in the email notifications, so just re-route to the single event view
  const scrollEventId = searchParams.get('e') ?? ''
  useEffect(() => {
    if (scrollEventId) {
      void navigate(`/communities/${communityId}/events/${scrollEventId}`)
    }
  }, [communityId, navigate, scrollEventId])

  const {
    data: communityPastEventsData,
    fetchMore: fetchMorePast,
    loading: loadingPast,
  } = useQuery(GetCommunityPastEventsDocument, {
    variables: { id: communityId || '', date: getStartOfCurrentDay(), pageSize: 25, userId: authUserId ?? '' },
    skip: !communityId,
    notifyOnNetworkStatusChange: true,
  })

  const pastPageInfo = communityPastEventsData?.community?.events?.pageInfo
  const pastHasMore = pastPageInfo?.hasNextPage ?? false
  const pastEndCursor = pastPageInfo?.endCursor

  const {
    data: communityUpcomingEventsData,
    fetchMore: fetchMoreUpcoming,
    loading: loadingUpcoming,
  } = useQuery(GetCommunityUpcomingEventsDocument, {
    variables: { id: communityId || '', date: getStartOfCurrentDay(), pageSize: 25, userId: authUserId ?? '' },
    skip: !communityId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })

  const upcomingPageInfo = communityUpcomingEventsData?.community?.events?.pageInfo
  const upcomingHasMore = upcomingPageInfo?.hasNextPage ?? false
  const upcomingEndCursor = upcomingPageInfo?.endCursor

  const isUpcomingTab = tab == EventTabs.Upcoming

  const communityEventsData = isUpcomingTab ? communityUpcomingEventsData : communityPastEventsData
  const hasMore = isUpcomingTab ? upcomingHasMore : pastHasMore
  const loadingEvents = isUpcomingTab ? loadingUpcoming : loadingPast
  const events = useMemo(() => {
    return communityUpcomingEventsData?.community?.events?.edges
      ?.map(e => {
        return e?.node
      })
      .sort((a, b) => {
        return a?.eventStart < b?.eventStart ? -1 : 1
      })
  }, [communityUpcomingEventsData?.community?.events?.edges])

  const nextLiveStreamEvent = useMemo(() => {
    // Try to match the selected event before the "next" event
    return (
      events?.find(e => {
        const now = new Date()
        const end = new Date(e?.eventEnd)
        const start = new Date(e?.eventStart)
        const hasStarted = start <= now
        return (
          e?.eventId == scrollEventId &&
          e?.isLivestream &&
          !e?.isRepost &&
          ((hasStarted && now <= end) || (isVeevan && e?.livestreamStart))
        )
      }) ??
      events?.find(e => {
        const now = new Date()
        const end = new Date(e?.eventEnd)
        return !e?.isRepost && e?.isLivestream && (now <= end || (isVeevan && e?.livestreamStart))
      })
    )
  }, [events, isVeevan, scrollEventId])

  const [isFutureLivestream, setIsFutureLivestream] = useState<boolean>(true)
  useEffect(() => {
    return nextLiveStreamEvent ? eventHasStartHandler(nextLiveStreamEvent, setIsFutureLivestream, isVeevan) : () => {}
  }, [setIsFutureLivestream, nextLiveStreamEvent, isVeevan, nextLiveStreamEvent?.livestreamStart])

  const liveStreamEvent = useMemo(() => {
    if (!isFutureLivestream) {
      return nextLiveStreamEvent
    }
  }, [isFutureLivestream, nextLiveStreamEvent])

  const liveStreamId = liveStreamEvent?.eventId ?? ''
  const hasLivestream = Boolean(liveStreamId)

  const loadMoreEvents = () => {
    if (isUpcomingTab) {
      void fetchMoreUpcoming({ variables: { cursor: upcomingEndCursor } })
    } else {
      void fetchMorePast({ variables: { cursor: pastEndCursor } })
    }
  }

  const [showCreateView, setShowCreateView] = useState<boolean>(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [eventsInEditMode, setEventsInEditMode] = useState<string[]>([])

  const canDelete =
    profileVisible && ((isCompany && isVeevan) || hasLeaderPermissions || actingSummitAdmin || actingSysAdmin)
  const canEditEvents = canDelete && (isUpcomingTab || actingSysAdmin)
  const canCreate = canDelete && !showCreateView

  const handleCreateClicked = (e: SyntheticEvent) => {
    setShowCreateView(true)
    elementClicked(e, 'click-community-event-create', { communityId: communityId })
  }

  const mappedEvents: Map<string, Maybe<Partial<EventPartsFragment>>[]> | undefined = useMemo(() => {
    if (communityEventsData) {
      const map = new Map<string, Maybe<Partial<EventPartsFragment>>[]>()
      const events = communityEventsData?.community?.events?.edges?.map(e => e?.node ?? null) ?? []
      for (const event of events) {
        const key = asDate(event?.eventStart)?.toLocaleDateString([], { month: 'long', year: 'numeric' }) || ''
        if (map.has(key)) map.get(key)?.push(event)
        else map.set(key, [event])
      }
      return map
    }
  }, [communityEventsData])

  const { data } = useQuery(GetNotificationsDocument, {
    variables: { recipientId: authUserId },
    skip: !authUserId,
  })

  const [markEventsViewedUpTo] = useMutation(MarkEventsViewedUpToDocument, { refetchQueries: ['GetNotifications'] })
  useEffect(() => {
    const eventNotifications = data?.notifications?.edges
      ?.map(e => e?.node)
      .map(asNotification)
      ?.filter(
        n =>
          n.notificationType === NotificationType.UpdEventTellMmbr ||
          n.notificationType === NotificationType.NewEventTellMmbr
      )
      .sort((a, b) => new Date(b?.created || '').getTime() - new Date(a?.created || '').getTime())
    const newestNotification = eventNotifications?.find(el => el !== undefined)
    if (newestNotification && communityId) {
      void markEventsViewedUpTo({
        variables: { notificationId: newestNotification?.notificationId, communityId: communityId },
      })
    }
  }, [communityId, markEventsViewedUpTo, data?.notifications?.edges])

  const handleEditingChange = (isEditing: boolean, eventId?: string | null) => {
    if (!eventId) return

    if (isEditing) {
      setEventsInEditMode([...eventsInEditMode, eventId])
    } else {
      setEventsInEditMode(eventsInEditMode.filter(e => e !== eventId))
    }
  }

  const editingInProgress = eventsInEditMode.length > 0

  const setToast = (s: string) => {
    setToastMessage(s)
    setShowToast(s != '')
  }

  useEffect(() => {
    const t = searchParams.get('tab')
    if (t === '0') {
      setTab(EventTabs.Upcoming)
    } else if (t === '1') {
      setTab(EventTabs.Past)
    }
  }, [searchParams])

  const tabs = (
    <Nav className={`tab-controls ${isMobile ? 'mobile' : ''}`}>
      <Nav.Item className={`tab-options`}>
        <button
          role="upcoming-events"
          className={`${isUpcomingTab ? ' selected' : ''}`}
          onClick={() => setTab(EventTabs.Upcoming)}
        >
          UPCOMING EVENTS
        </button>
      </Nav.Item>
      <Nav.Item className={`tab-options`}>
        <button
          role="past-events"
          className={`${tab == EventTabs.Past ? ' selected' : ''}`}
          onClick={() => setTab(EventTabs.Past)}
        >
          PAST EVENTS
        </button>
      </Nav.Item>
      {hasLivestream && (
        <Nav.Item className={`tab-options`}>
          <button
            role="live-stream"
            className={`${tab == EventTabs.LiveStream ? ' selected' : ''} live-stream`}
            onClick={() => setTab(EventTabs.LiveStream)}
          >
            LIVE
          </button>
        </Nav.Item>
      )}
    </Nav>
  )

  if (!mappedEvents || memberLoading || authLoading)
    return (
      <div className={'events-container'}>
        <div className="top-items">{tabs}</div>
        <div className={'loading'}>Loading...</div>
      </div>
    )

  return (
    <div className={'events-container'}>
      <div className={`top-items ${breakpoint <= SizeBreakpoint.md ? 'mobile' : ''}`}>
        {tabs}
        {canCreate && (
          <Button onClick={handleCreateClicked} disabled={editingInProgress}>
            Create Event
          </Button>
        )}
      </div>
      {tab != EventTabs.LiveStream && !showCreateView && mappedEvents.size === 0 && (
        <div className="no-events">This community has no {isUpcomingTab ? 'upcoming' : 'past'} events</div>
      )}
      {showCreateView && (
        <CommunityEventEdit
          communityId={communityId ?? ''}
          onClose={() => setShowCreateView(false)}
          setToast={setToast}
        />
      )}
      {tab == EventTabs.LiveStream && hasLivestream ? (
        <>
          <CommunityEventRow
            canEdit={canEditEvents && !liveStreamEvent?.repost?.eventId}
            eventId={liveStreamEvent?.eventId}
            communityId={communityId ?? ''}
            setToast={setToast}
            isVeevan={isVeevan}
            canRepost={isVeevan && !isCompany && !isPrivate && !liveStreamEvent?.draft}
            onEditingChange={handleEditingChange}
            canDelete={canDelete}
            currentLiveStreamEventId={liveStreamEvent?.eventId}
            turnOffLivestream={() => setTab(EventTabs.Upcoming)}
          />
          {Boolean(liveStreamEvent?.qAndAEnabled) && <LiveStreamQA eventId={liveStreamId} />}
        </>
      ) : (
        <>
          <div className="upcoming-events">
            {Array.from(mappedEvents.keys()).map(key => (
              <div className="month-section" key={key}>
                <h6>{key}</h6>
                {mappedEvents.get(key)?.map(event => (
                  <CommunityEventRow
                    key={event?.eventId}
                    canEdit={canEditEvents && !event?.repost?.eventId}
                    eventId={event?.eventId}
                    communityId={communityId ?? ''}
                    setToast={setToast}
                    isVeevan={isVeevan}
                    canRepost={isVeevan && !isCompany && !isPrivate && isUpcomingTab && !event?.draft}
                    onEditingChange={handleEditingChange}
                    canDelete={canDelete}
                    joinLivestream={() => {
                      if (liveStreamEvent?.eventId && event?.eventId === liveStreamEvent?.eventId && !event?.isRepost) {
                        setTab(EventTabs.LiveStream)
                      } else {
                        const navEvent = event?.isRepost ? event?.repost : event
                        void navigate(`/communities/${navEvent?.communityId}/events/${navEvent?.eventId}`)
                      }
                    }}
                    currentLiveStreamEventId={liveStreamEvent?.eventId}
                  />
                ))}
              </div>
            ))}
          </div>
          {loadingEvents ? (
            <div className={'loading show-more-loading'}>Loading...</div>
          ) : (
            hasMore && (
              <div className="show-more">
                <Button size={'sm'} onClick={loadMoreEvents}>
                  Show more
                </Button>
              </div>
            )
          )}
        </>
      )}
      <ToastComponent show={showToast} onClose={() => setShowToast(false)}>
        {toastMessage ?? ''}
      </ToastComponent>
    </div>
  )
}

export default CommunityEvents
